/**
 * useBPAFindings
 *
 * Custom hook that coordinates data fetching and state updates for the
 * BPA Findings listing feature.
 *
 * Responsibilities:
 *  - Watch `findingsDataQueryParamsAtom` for changes (filters, pagination,
 *    ordering, refresh triggers).
 *  - Invoke the service layer and write results into `findingsDataAtom`.
 *  - Guard against fetching before query-string filters are hydrated.
 *  - Expose a manual `refresh()` helper.
 */

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { extractApiErrorMessage } from 'lib/helper';
import { showFailureToast } from 'lib/ToastHelper';
import { useEffect } from 'react';

import { fetchBPAFindings } from '../services/bpaFindingsService';
import {
  findingsDataAtom,
  findingsDataQueryParamsAtom,
  findingsDataRefreshAtom,
  isQuerystringFiltersInitializedAtom,
} from '../state';

export function useBPAFindings() {
  const queryParams = useAtomValue(findingsDataQueryParamsAtom);
  const isInitialized = useAtomValue(isQuerystringFiltersInitializedAtom);
  const [listingData, setListingData] = useAtom(findingsDataAtom);
  const setRefresh = useSetAtom(findingsDataRefreshAtom);

  useEffect(() => {
    // Wait until URL query-string filters have been read and applied
    if (!isInitialized) return;

    let cancelled = false;

    const loadData = async () => {
      // Functional update avoids capturing stale `listingData` in the closure
      // and keeps it out of the effect's dependency array.
      setListingData((prev) => ({ ...prev, loading: true }));

      try {
        const response = await fetchBPAFindings(queryParams);

        if (!cancelled) {
          setListingData({
            results: response.results ?? [],
            count: response.count ?? 0,
            loading: false,
          });
        }
      } catch (err) {
        if (!cancelled) {
          showFailureToast(
            extractApiErrorMessage(err, 'Failed to load BPA findings.'),
          );
          setListingData({
            results: [],
            count: 0,
            loading: false,
          });
        }
      }
    };

    void loadData();

    // Cancel state updates if the effect re-runs before the previous fetch
    // resolves (prevents stale writes on rapid filter changes).
    return () => {
      cancelled = true;
    };
  }, [queryParams, isInitialized, setListingData]);

  /** Force a fresh API call without changing any filter/pagination atoms */
  const refresh = () => setRefresh(new Date().toISOString());

  return {
    data: listingData.results,
    count: listingData.count,
    loading: listingData.loading,
    refresh,
  };
}
